import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import learningMaterialService, { LearningMaterial } from '../../services/learningMaterialService';

interface ExerciseMaterialSelectorProps {
  lessonId: number;
  materialId?: number;
  showMaterialToStudent?: boolean;
  onChange: (materialId?: number, showMaterialToStudent?: boolean) => void;
}

const ExerciseMaterialSelector: React.FC<ExerciseMaterialSelectorProps> = ({
  lessonId,
  materialId,
  showMaterialToStudent = false,
  onChange,
}) => {
  const [materials, setMaterials] = useState<LearningMaterial[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadMaterials = async () => {
      setIsLoading(true);
      try {
        setMaterials(await learningMaterialService.getMaterialsByLesson(lessonId));
      } catch (error) {
        console.error('Failed to load learning materials:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMaterials();
  }, [lessonId]);

  const handleMaterialChange = (selectedMaterialId: number | '') => {
    if (selectedMaterialId === '') {
      onChange(undefined, false);
      return;
    }

    onChange(selectedMaterialId, true);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Oppitunnin materiaali
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Liitä harjoitukseen oppitunnin materiaali. Opiskelija voi käyttää sitä tehtävää tehdessään.
      </Typography>
      <FormControl fullWidth>
        <InputLabel id="exercise-material-select-label">Materiaali</InputLabel>
        <Select
          labelId="exercise-material-select-label"
          label="Materiaali"
          value={materialId || ''}
          onChange={(event) => handleMaterialChange(event.target.value as number | '')}
          disabled={isLoading}
        >
          <MenuItem value="">Ei materiaalia</MenuItem>
          {materials.map((material) => (
            <MenuItem key={material.id} value={material.id}>
              {material.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {materials.length === 0 && !isLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Oppitunnilla ei ole vielä materiaalia.
        </Alert>
      )}
      {materialId && (
        <FormControlLabel
          sx={{ mt: 1 }}
          control={(
            <Checkbox
              checked={showMaterialToStudent}
              onChange={(event) => onChange(materialId, event.target.checked)}
            />
          )}
          label="Näytä materiaali opiskelijalle"
        />
      )}
    </Box>
  );
};

export default ExerciseMaterialSelector;
